import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import tokenService from '../../utils/tokenService';

const TokenManager = ({ children }) => {
    const navigate = useNavigate();

    useEffect(() => {
        // Initialize token manager on app start
        const initializeTokenManager = async () => {
            const token = tokenService.getToken();
            
            if (token) {
                console.log('🔐 Token found, validating...');
                
                // Validate token on app start
                const isValid = await tokenService.validateToken();
                
                if (!isValid) {
                    console.log('🔐 Invalid token, attempting refresh...');
                    const refreshed = await tokenService.refreshToken(true);
                    
                    if (!refreshed) {
                        console.log('🔐 Refresh failed, allowing guest access');
                        tokenService.clearToken();
                        // Don't redirect to login - allow guest access
                    }
                } else {
                    console.log('✅ Token is valid');
                }
            }
        };

        initializeTokenManager();

        // Handle browser focus/visibility changes (mobile browsers)
        const handleVisibilityChange = async () => {
            if (!document.hidden && tokenService.isLoggedIn()) {
                console.log('📱 App became visible - checking token...');
                
                // Add a small delay to ensure app is fully loaded
                setTimeout(async () => {
                    const isValid = await tokenService.validateToken();
                    
                    if (!isValid) {
                        console.log('🔐 Token invalid after visibility change, refreshing...');
                        const refreshed = await tokenService.refreshToken(true);
                        
                        if (!refreshed) {
                            console.log('🔐 Session expired - allowing guest access');
                            tokenService.clearToken();
                            // Don't redirect to login - allow guest access
                        }
                    }
                }, 500);
            }
        };

        // Handle app going to background/foreground (mobile)
        const handlePageShow = async (event) => {
            if (event.persisted && tokenService.isLoggedIn()) {
                console.log('📱 Page restored from bfcache - refreshing token...');
                await tokenService.refreshToken(true);
            }
        };

        // Handle network reconnection
        const handleOnline = async () => {
            if (tokenService.isLoggedIn()) {
                console.log('🌐 Network reconnected - validating token...');
                const isValid = await tokenService.validateToken();
                
                if (!isValid) {
                    await tokenService.refreshToken(true);
                }
            }
        };

        // Add event listeners
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('pageshow', handlePageShow);
        window.addEventListener('online', handleOnline);

        // Cleanup
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('pageshow', handlePageShow);
            window.removeEventListener('online', handleOnline);
        };
    }, [navigate]);

    // Enhanced error boundary for token-related errors
    useEffect(() => {
        const handleUnhandledRejection = (event) => {
            if (event.reason?.response?.status === 401) {
                console.log('🔐 Unhandled 401 error - clearing token, allowing guest access');
                tokenService.clearToken();
                // Don't redirect to login - allow guest access
            }
        };

        window.addEventListener('unhandledrejection', handleUnhandledRejection);
        
        return () => {
            window.removeEventListener('unhandledrejection', handleUnhandledRejection);
        };
    }, [navigate]);

    return children;
};

export default TokenManager;